import BigNumber from 'bignumber.js';
import { formatInTimeZone } from 'date-fns-tz';
import { Atom, useAtomValue } from 'jotai';
import { ReactNode, Suspense, createRef } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import BoltIcon from '@flipster/react/icon/generated/BoltIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { Styles, css } from '@flipster/styled-system/css';
import { center, hstack } from '@flipster/styled-system/patterns';
import { SparkPointPrecision, UsdtPrecision } from '@flipster/utils/constants/currency';
import { numberFormatter } from '@flipster/utils/num';
import { ProdStaticPath } from '@flipster/utils/url';

import {
  Dialog,
  DialogBottom,
  DialogButton,
  DialogContents,
  DialogDescription,
  DialogTitle,
  DialogTopBar,
  type DialogRef,
} from '../../utils/Dialog';
import { Portal } from '../../utils/Portal';
import { button } from '../../utils/button';

export const SparkRewardSummary = ({
  css: cssProp,
  ended,
  DepositCta = 'button',
  MultiplierCta = 'button',
  userPointAtom,
  updatedAtAtom,
  earnedYesterdayAtom,
  multiplierRulesAtom,
  usdtBalanceAtom,
}: {
  css?: Styles;
  ended: boolean;
  DepositCta?: string | ((props: { className?: string; children?: ReactNode }) => ReactNode);
  MultiplierCta?:
    | string
    | ((props: { className?: string; children?: ReactNode; onClick?: () => unknown }) => ReactNode);
  userPointAtom: Atom<BigNumber | undefined>;
  updatedAtAtom: Atom<Date | undefined>;
  earnedYesterdayAtom: Atom<BigNumber | undefined>;
  multiplierRulesAtom: Atom<
    | {
        multiplier: number;
        balanceThreshold: number;
      }[]
    | undefined
  >;
  usdtBalanceAtom: Atom<BigNumber | undefined>;
}) => {
  const multiplierDialogRef = createRef<DialogRef>();
  const userPoint = useAtomValue(userPointAtom);
  const updatedAt = useAtomValue(updatedAtAtom);
  const earnedYesterday = useAtomValue(earnedYesterdayAtom);
  const multiplierRules = useAtomValue(multiplierRulesAtom);
  const usdtBalance = useAtomValue(usdtBalanceAtom);

  const multiplierDesc = multiplierRules?.sort((a, b) => (a.balanceThreshold > b.balanceThreshold ? -1 : 1));
  const currentMultiplier =
    usdtBalance && multiplierDesc?.find((x) => usdtBalance.isGreaterThanOrEqualTo(x.balanceThreshold))?.multiplier;
  const maxMultiplier = multiplierDesc?.at(0)?.multiplier;

  return (
    <>
      <section
        className={css(
          { backgroundColor: 'background.default.level-0', p: 16, rounded: 12, textAlign: 'center' },
          cssProp,
        )}
      >
        <div
          className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1', display: 'block', mb: 12 })}
        >
          <FormattedMessage
            id='Autogen_common.r251126.s_Latest_update_updatedAt'
            values={{
              updatedAt: updatedAt && (
                <time dateTime={updatedAt.toString()}>
                  {formatInTimeZone(updatedAt, 'Etc/UTC', 'yy.MM.dd, HH:mm')} UTC
                </time>
              ),
            }}
          />
        </div>

        <div
          className={css({
            backgroundColor: 'background.default.level-1',
            color: 'content.default.level-5',
            py: 8,
            mb: 16,
            rounded: 4,
          })}
        >
          <h2 className={css({ textStyle: 'Text/L/Strong' })}>
            <FormattedMessage id='Autogen_common.r251126.s_Total_sparks' />
          </h2>
          <p className={hstack({ justify: 'center', textStyle: 'Title/L', gap: 6 })}>
            <BoltIcon className={css({ verticalAlign: 'middle' })} />
            <span>{userPoint ? numberFormatter(userPoint.toString(), { precision: SparkPointPrecision }) : 0}</span>
          </p>
        </div>

        <dl
          className={css({
            display: 'grid',
            gridTemplateColumns: 'auto auto',
            rowGap: 8,
            mb: 8,
            '& > dt': {
              display: 'flex',
              alignItems: 'center',
              textAlign: 'left',
              textStyle: 'Text/L/Strong',
              color: 'content.default.level-1',
            },
            '& > dd': { textAlign: 'right', textStyle: 'Text/L/Strong', color: 'content.default.level-5' },
          })}
        >
          <dt>
            <FormattedMessage id='Autogen_common.r251126.s_Earned_yesterday' />
          </dt>
          <dd>
            <span>
              <span className={css({ color: 'content.accent.info' })}>
                {earnedYesterday ? (
                  <span>+{numberFormatter(earnedYesterday.toString(), { precision: SparkPointPrecision })}</span>
                ) : (
                  0
                )}
              </span>{' '}
              <span>
                <FormattedMessage id='Autogen_common.r251126.s_Sparks' />
              </span>
            </span>
          </dd>

          <dt
            className={css({
              '& button': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'currentcolor',
                marginLeft: '4px',
              },
            })}
          >
            <span>
              <FormattedMessage id='Autogen_common.r251126.s_Current_multiplier' />
            </span>
            <MultiplierCta className={center()} onClick={() => multiplierDialogRef.current?.open()}>
              <InformationCircleIcon />
            </MultiplierCta>
          </dt>
          <dd>
            {currentMultiplier ? (
              <span className={css({ color: 'content.accent.info' })}>{currentMultiplier}x</span>
            ) : (
              <span className={css({ color: 'content.accent.info' })}>
                <FormattedMessage id='Autogen_common.r251126.s_Deposit_required' />
              </span>
            )}
          </dd>
          <dt>
            <img
              alt='USDT logo'
              className={css({ verticalAlign: 'middle', mr: 4 })}
              src={`${ProdStaticPath}/images/wallet/logo/usdt.svg`}
              width={20}
              height={20}
            />
            <div>
              <FormattedMessage id='Autogen_common.r251126.s_USDT_balance' />
            </div>
          </dt>
          <dd>{usdtBalance && numberFormatter(usdtBalance.toString(), { precision: UsdtPrecision, toFixed: true })}</dd>
        </dl>

        <div className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-1' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Multiplier_is_based_on_your_USDT_balance' />
        </div>

        {!ended && maxMultiplier && currentMultiplier !== maxMultiplier && (
          <DepositCta className={css(button.raw({ size: 'large', display: 'flex', mode: 'light' }), { mt: 16 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Deposit_to_upgrade_multiplier' />
          </DepositCta>
        )}
      </section>
      <Suspense>
        <Portal>
          <Dialog ref={multiplierDialogRef} behavior='popup'>
            <DialogTopBar />
            <DialogContents>
              <DialogTitle>
                <FormattedMessage id='Autogen_common.r251126.s_Current_multiplier' />
              </DialogTitle>
              <DialogDescription css={css.raw({ color: 'content.default.level-2' })}>
                <FormattedMessage id='Autogen_common.r251126.s_The_multiplier_for_your_points_is_determined_by_your_USDT_balance_Higher_balances_result_in_higher_multipliers' />
              </DialogDescription>
              <ul>
                {multiplierDesc
                  ?.sort((a, b) => (a.balanceThreshold > b.balanceThreshold ? 1 : -1))
                  .map((x) => (
                    <li
                      key={x.multiplier}
                      className={css({
                        position: 'relative',
                        paddingLeft: 18,
                        _before: {
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          left: 6,
                          content: "'•'",
                          color: 'content.default.level-5',
                        },
                      })}
                    >
                      <div
                        className={css({
                          textStyle: ['Text/S/Strong', 'Text/L/Regular'],
                          color: 'content.default.level-5',
                        })}
                      >
                        <FormattedMessage
                          id='Autogen_common.r251126.s_x_multiplier_x_multiplier'
                          values={{
                            x_multiplier: x.multiplier,
                          }}
                        />
                      </div>
                      <div
                        className={css({
                          textStyle: ['Text/S/Regular', 'Text/L/Regular'],
                          color: 'content.default.level-2',
                        })}
                      >
                        <FormattedMessage
                          id='Autogen_common.r251126.s_Your_balance_numberFormatter_USDT'
                          values={{
                            numberFormatter: numberFormatter(x.balanceThreshold, { withComma: true }),
                          }}
                        />
                      </div>
                    </li>
                  ))}
              </ul>
            </DialogContents>
            <DialogBottom>
              <DialogButton type='button' onClick={() => multiplierDialogRef.current?.close()}>
                OK
              </DialogButton>
            </DialogBottom>
          </Dialog>
        </Portal>
      </Suspense>
    </>
  );
};
